'use client';

import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import scrollToSectionId from '@alltrails/core/utils/scrollToSectionId';
import { ratingToFormattedRating } from '@alltrails/data-formatting/numberFormatters';
import Link from '@alltrails/denali/components/Link';
import type { Size } from '@alltrails/denali/types';
import type { TrailId } from '@alltrails/types/TrailId';
import { REVIEWS_SECTION_ID } from '@/utils/constants';
import styles from './Rating.module.scss';

type RatingProps = {
  rating: number;
  reviewCount: number;
  size?: Size<'sm' | 'md'>;
  trailId: TrailId;
  seoTestHideAllReviewsForLoggedOutUsers?: boolean;
};

const Rating = ({ rating, reviewCount, size = 'md', trailId, seoTestHideAllReviewsForLoggedOutUsers }: RatingProps) => {
  const intl = useIntl();
  const localizedRating = ratingToFormattedRating(rating, intl);

  const onRatingClick = () => {
    logTrailDetailsActionTapped({
      trail_id: String(trailId),
      trail_page_action: 'show all reviews',
      trail_page_location: 'header bar'
    });
    scrollToSectionId(REVIEWS_SECTION_ID);
  };

  return (
    <div className={classNames(styles.ratingAndReviews, styles.ratingLink, styles[size])}>
      <span data-testid="trail-rating">{localizedRating}</span>
      <div>
        (
        {seoTestHideAllReviewsForLoggedOutUsers ? (
          <FormattedMessage defaultMessage="{reviewCount, plural, one {# review} other {# reviews}}" values={{ reviewCount }} />
        ) : (
          <Link className={classNames(styles.ratingLink, styles[size])} onClick={onRatingClick}>
            <FormattedMessage defaultMessage="{reviewCount, plural, one {# review} other {# reviews}}" values={{ reviewCount }} />
          </Link>
        )}
        )
      </div>
    </div>
  );
};

export default Rating;
